import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatDate } from '../utils/dateFormat';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  FileText,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Eye
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CaseNotesPage = () => {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteTypeFilter, setNoteTypeFilter] = useState('');
  const [noteTypes, setNoteTypes] = useState([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 20;

  useEffect(() => {
    fetchNoteTypes();
  }, []);

  useEffect(() => {
    fetchNotes();
  }, [page, noteTypeFilter]);

  const fetchNoteTypes = async () => {
    try {
      const response = await axios.get(`${API}/dropdowns/note_types`);
      if (response.data.items && response.data.items.length > 0) {
        setNoteTypes(response.data.items);
      } else {
        setNoteTypes([
          { value: 'progress', label: 'Progress Note' },
          { value: 'intake', label: 'Intake Assessment' },
          { value: 'crisis', label: 'Crisis Intervention' },
          { value: 'follow_up', label: 'Follow-up' },
          { value: 'referral', label: 'Referral' },
        ]);
      }
    } catch (error) {
      console.error('Failed to fetch note types:', error);
      setNoteTypes([
        { value: 'progress', label: 'Progress Note' },
        { value: 'intake', label: 'Intake Assessment' },
        { value: 'crisis', label: 'Crisis Intervention' },
        { value: 'follow_up', label: 'Follow-up' },
        { value: 'referral', label: 'Referral' },
      ]);
    }
  };

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: page * limit,
        limit: limit.toString()
      });
      if (noteTypeFilter) params.append('note_type', noteTypeFilter);

      const response = await axios.get(`${API}/case-notes?${params}`);
      setNotes(response.data.items);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6" data-testid="case-notes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Case Notes</h1>
          <p className="text-muted-foreground mt-1">
            View all case documentation across the system
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className="card-shadow">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={noteTypeFilter || "all"} onValueChange={(val) => setNoteTypeFilter(val === "all" ? "" : val)}>
              <SelectTrigger className="w-full sm:w-48" data-testid="note-type-filter">
                <SelectValue placeholder="All Note Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Note Types</SelectItem>
                {noteTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card className="card-shadow">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Notes</CardTitle>
            <span className="text-sm text-muted-foreground">{total} notes found</span>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-48" data-testid="loading-spinner">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : notes.length > 0 ? (
            <>
              <div className="space-y-4">
                {notes.map((note) => (
                  <div 
                    key={note.id} 
                    className="p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/cases/${note.case_id}`, { state: { activeTab: 'notes' } })}
                    data-testid={`note-row-${note.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="capitalize">{note.note_type}</Badge>
                          <Badge className={`status-${note.status}`}>{note.status}</Badge>
                          <span className="text-sm text-muted-foreground font-mono">{note.case_id}</span>
                        </div>
                        <p className="font-medium mb-1">{note.participant_name}</p>
                        <p className="text-sm text-muted-foreground line-clamp-2">{note.content}</p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/cases/${note.case_id}`, { state: { activeTab: 'notes' } });
                        }}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                    </div>
                    <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                      <span>Service Date: {formatDate(note.date_of_service) || '-'}</span>
                      <span>•</span>
                      <span>By {note.author_name}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-4 border-t border-border mt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                      disabled={page >= totalPages - 1}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12" data-testid="no-results">
              <FileText className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
              <h3 className="text-lg font-medium">No notes found</h3>
              <p className="text-muted-foreground mt-1">
                Case notes can be added from within a case&apos;s Notes tab
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CaseNotesPage;
